<template>
  <div class="box-plot-container">
        <div ref="plot">
        </div>
        <div class="tooltip" ref="tooltip"></div>
    </div>
</template>

<script>
import * as d3 from "d3";
import _ from "lodash";

// linear congruential generator
function LCG(seed) {
    return function() {
        seed = Math.imul(48271, seed) | 0 % 2147483647;
        return (seed & 2147483647) / 2147483648;
    };
}

function boxPlotStats(arr) {
    arr.sort(d3.ascending);
    let q1 = d3.quantile(arr, 0.25);
    let q2 = d3.quantile(arr, 0.5);
    let q3 = d3.quantile(arr, 0.75);
    let iqr = q3 - q1; // Interquartile range
    let r0 = Math.max(q1 - 1.5 * iqr, d3.min(arr));
    let r4 = Math.min(q3 + 1.5 * iqr, d3.max(arr));
    return [r0, q1, q2, q3, r4, iqr];
}

export default {
    props: ['input', 'meta', 'selectedAnno'],
    watch: {
        input() {
            this.render();
        },
        selectedAnno() {
            this.render();
        },
    },
    data() {
        return {
            title: null,
        }
    },
    methods: {

        boxplot(el, data, rank) {
            let width = 420,
                height = 420,
                margin = {top: 80, right: 30, bottom: 70, left: 60},
                plotWidth = width - margin.left - margin.right,
                plotHeight = height - margin.top - margin.bottom;

            let svg = d3.select(el).append("svg")
                .attr("width", width)
                .attr("height", height)
                // .attr("style", "background-color: white")
                // .attr("transform", "translate(0," + (rank * 48.19 - 80) + ")");

            let wrap = svg.append('g');
                // .attr("transform", "translate(0," + (rank * 48.19 - 80) + ")");

            let plot = wrap.append("g")
                .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

            ////////////////////// FACET & TITLE

            plot.append("rect")
                .attr("width", plotWidth)
                .attr("height", plotHeight)
                .style("stroke", "#808080")
                .style("fill", "none");

            let titleData = data;

            let title = wrap.append("rect")
                .attr("x", margin.left)
                .attr("y", 0)
                .attr("width", plotWidth)
                .attr("height", margin.top)
                .style("fill", "#d3d3d3")
                .style("stroke", "#808080");

            let titleText = wrap.append("text")
                .attr("x", 20)
                // .attr("pointer-events", "none") // Commented out to allow clicking on the copy icon
                .style("text-anchor", "start")
                .style("alignment-baseline", "hanging");

            let lineHeight = 1.2;

            let idText = titleText.append("tspan")
                .attr("x", margin.left + 10)
                .attr("dy", "1em")
                .style("font-weight", "bold");

            let traitId = titleData.molecular_trait_id;
            let truncatedTraitId = traitId;
            let fontSize = 16;
            let charactersFit = Math.floor(plotWidth / fontSize * 2) - 10; // 2 is an approximate width of a character

            if (traitId.length > charactersFit) {
                truncatedTraitId = traitId.substring(0, charactersFit - 3) + "..."; // -3 to account for the "..."
            }
            idText.text(truncatedTraitId)
                    .on("mouseover", (event) => {
                        d3.select(this.$refs["tooltip"])
                            .style("opacity", 1)
                            .style("left", (event.pageX- 100) + "px")  // an approximate position of the tooltip
                            .style("top", (event.pageY-330) + "px")
                            .html(traitId); // Set the full traitId as the tooltip content
                    })
                    .on("mouseout", () => {
                        d3.select(this.$refs["tooltip"]).style("opacity", 0); // Hide the tooltip
                    });
            
            //Add copy icon
            let copyIconGroup = wrap.append("g")
            .attr("class", "copy-icon-group")
            .attr("transform", "translate(" + (margin.left + 300) + ", 0)")
            .style("cursor", "pointer")
            .on("click", () => this.copyToClipboard(traitId, copyIconGroup))
            .on("mouseover", function() { 
                d3.select(this).select("title").text("Copy");
            })
            .on("mouseout", function() { 
                d3.select(this).select("title").text("");
            });
            
            // Add the copy icon to the group
            copyIconGroup.append("text")
            .attr("class", "copy-icon")
            .attr("x", 0)
            .attr("dy", "1em")
            .style("font-size", "16px")
            .text("📋");
            
            // Add tooltip (title element)
            copyIconGroup.append("title");
            
            let subTitleText = wrap.append("text")
                .attr("x", 20)
                .attr("y", `1em`)
                .style("text-anchor", "start")
                .style("alignment-baseline", "hanging");

            let lines = [`beta: ${titleData.beta}`, `se: ${titleData.se}`, `p-value: ${titleData.pvalue}`];
            lines.forEach((line, i) => {
                subTitleText.append("tspan")
                    .attr("x", margin.left + 10)
                    .attr("dy", `${lineHeight}em`)
                    .text(line);
            });



            ////////////////////// AXES & GRID

            // Define the x and y scales
            let xScale = d3.scaleBand()
                .domain(["abs_gt0", "abs_gt1", "abs_gt2"])
                .range([0, plotWidth])
                .padding(0.1);

            let allData = [].concat(data.abs_gt0, data.abs_gt1, data.abs_gt2);


            let yScale = d3.scaleLinear()
                .domain([-0.1 * d3.max(allData), 1.1*d3.max(allData)]) // Adjust the lower limit of the domain
                .range([plotHeight, 0]);

            // Add the x and y axes
            plot.append("g")
                .attr("transform", "translate(0," + plotHeight + ")")
                .call(d3.axisBottom(xScale));

            plot.append("g")
                .call(d3.axisLeft(yScale));

            // plot.append("g")
            //     .call(d3.axisLeft(yScale).ticks(6));

            plot.append("text")
                .attr("transform", "rotate(-90)")
                .attr("y", 0 - margin.left)
                .attr("x", 0 - (plotHeight / 2))
                .attr("dy", "1.2em")
                .style("text-anchor", "middle")
                .text("Transcripts Per Million (TPM)");

            // Add gridlines for the y axis with alternating thickness
            let yGridlines = d3.axisLeft(yScale)
                .tickSize(-plotWidth)
                .tickFormat("");

            plot.append("g")
                .attr("class", "grid")
                .call(yGridlines)
                .selectAll(".tick line")
                .attr("stroke-width", function(d, i) { return i % 2 ? 1 : 0.5; })
                .attr("stroke", "#ddd"); // Change color to lighter grey

            // Add gridlines for the x axis
            let xGridlines = d3.axisBottom(xScale)
                .tickSize(plotHeight)
                .tickFormat("");

            plot.append("g")
                .attr("class", "grid")
                // .attr("transform", "translate(0," + plotHeight + ")")
                .call(xGridlines)
                .selectAll(".tick line")
                .attr("stroke-width", 1)
                .attr("stroke", "#ddd"); // Change color to lighter grey


            ////////////////////// DOTS
            let seededRandom = LCG(12345);

            // Add the data points as circles
            let categories = ["abs_gt0", "abs_gt1", "abs_gt2"];
            categories.forEach((category, i) => {
                if (data[category]) {
                    plot.selectAll()
                        .data(data[category])
                        .enter()
                        .append("circle")
                        .attr("cx", d => xScale(category) + seededRandom() * xScale.bandwidth() / 2 + xScale.bandwidth() / 4)
                        .attr("cy", d => yScale(d))
                        .attr("r", 3)  // Adjust the radius to change the size of the dots
                        .style("fill", "rgba(150, 150, 150, 0.3)")  // Light grey fill with 50% transparency
                        .style("stroke", "white");
                }
            });

            ////////////////////// BOXPLOT

            categories.forEach((category, i) => {
                    if (data[category]) {
                        let s = boxPlotStats(data[category]);

                        // Box
                        plot.append("rect")
                            .attr("x", xScale(category))
                            .attr("y", yScale(s[3]))
                            .attr("height", yScale(s[1]) - yScale(s[3]))
                            .attr("width", xScale.bandwidth())
                            .style("fill", "rgba(70, 130, 180, 0.5)") // Light blue fill with 50% transparency
                            .style("stroke", "royalblue")
                            .style("stroke-width", 2);

                        // Line for median (Q2)
                        plot.append("line")
                            .attr("x1", xScale(category))
                            .attr("x2", xScale(category) + xScale.bandwidth())
                            .attr("y1", yScale(s[2]))
                            .attr("y2", yScale(s[2]))
                            .style("stroke", "royalblue")
                            .style("stroke-width", (yScale(s[2]) === yScale(s[0]) || yScale(s[2]) === yScale(s[3])) ? 3 : 2);

                        // Lines for whiskers
                        plot.append("line")
                            .attr("x1", xScale(category) + xScale.bandwidth() / 2)
                            .attr("x2", xScale(category) + xScale.bandwidth() / 2)
                            .attr("y1", yScale(s[0]))
                            .attr("y2", yScale(s[1]))
                            .style("stroke", "royalblue")
                            .style("stroke-width", 2);

                        plot.append("line")
                            .attr("x1", xScale(category) + xScale.bandwidth() / 2)
                            .attr("x2", xScale(category) + xScale.bandwidth() / 2)
                            .attr("y1", yScale(s[3]))
                            .attr("y2", yScale(s[4]))
                            .style("stroke", "royalblue")
                            .style("stroke-width", 2);

                    }
            });

        },
        copyToClipboard(text, iconGroup) {
            navigator.clipboard.writeText(text).then(() => {
                let isCopied = iconGroup.select(".copy-icon").text() === "✓";
                let newIcon = isCopied ? "📋" : "✓";
                let newTooltip = isCopied ? "Copy" : "Copied";
                this.$store.dispatch('addId', text);

                iconGroup.select(".copy-icon").text(newIcon);
                iconGroup.select("title").text(newTooltip);
            }).catch(err => {
                console.error('Could not copy text: ', err);
            });
        },
        render() {
            const el = this.$refs['plot'];
            el.innerHTML = ''; // clear

            if (this.input && this.selectedAnno) {
                // this.title = "Usage of introns in cluster";
                _.each(this.input.boxplots, v => {
                    if (v.molecular_trait_id === this.selectedAnno.label) {
                        this.boxplot(el, v, this.selectedAnno.rank)
                    }
                });
            }
        }
    }
}
</script>

<style scoped>
h5 {
    text-align: center;
    margin-top: 2em;
    margin-bottom: 1em;
}

.tooltip {
    position: absolute;
    opacity: 0;
    pointer-events: none;

    background-color: white;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    padding: 8px;
    text-align: center;
}

.box-plot-container {
    flex: 1 0 auto; 
    margin-top: 150px; 
}

svg {
    display: block;
    width: 100%;
    height: auto; /* Ensure the SVG scales properly */
}

</style>

<style>
</style>